import React, { useState } from 'react';
import styled, { createGlobalStyle, keyframes } from 'styled-components';
import ko from '../../language/Ko-kr.json';
import en from '../../language/En-eng.json';
import { ResponsiveShell } from '../../components/ResponsiveShell';

const isKorean = true; // TODO: 실제 언어 설정 연동
const t = isKorean ? ko : en;

/* ─── Global ─────────────────────────────────────────────────────────────── */
const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Jua&family=Noto+Sans+KR:wght@400;500;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { height: 100%; overflow: hidden; }
  body {
    font-family: 'Noto Sans KR', sans-serif;
    background: #E8E0C8;
    color: #5C5040;
    -webkit-font-smoothing: antialiased;
    height: 100dvh;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  #root {
    width: 100%;
    height: 100dvh;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
  }
`;

/* ─── Animations ─────────────────────────────────────────────────────────── */
const slideUp = keyframes`
  from { transform: translateY(100%); opacity: 0.5; }
  to { transform: translateY(0); opacity: 1; }
`;

/* ─── Phone Shell ────────────────────────────────────────────────────────── */

/* ─── Nav Bar ────────────────────────────────────────────────────────────── */
const PhoneNavBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 20px 12px;
  flex-shrink: 0;
  background: rgba(249, 246, 232, 0.95);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1.5px solid #EDE9D5;
  z-index: 10;
`;

const BackBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #EDE9D5;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.2s, transform 0.15s;
  color: #5C5040;
  &:hover { background: #E0D8C0; transform: scale(1.05); }
  &:active { transform: scale(0.95); }
`;

const NavTitle = styled.h2`
  font-family: 'Jua', sans-serif;
  font-size: 18px;
  color: #3D3020;
  flex: 1;
  text-align: center;
  margin-right: 36px; /* offset for back button to center title */
`;

/* ─── Body ───────────────────────────────────────────────────────────────── */
const PhoneBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 32px 24px;
`;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 32px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InputLabel = styled.label`
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #5C5040;
`;

const StyledInput = styled.input`
  width: 100%;
  border: 1.5px solid #D8D0B8;
  border-radius: 16px;
  background: #FFF;
  padding: 14px 18px;
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 15px;
  color: #3D3020;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  
  &:focus {
    border-color: #E07040;
    box-shadow: 0 0 0 3px rgba(224, 112, 64, 0.1);
  }
  
  &::placeholder {
    color: #C8C0A8;
  }
`;

const PrimaryBtn = styled.button`
  background: #E07040; 
  color: #fff;
  border: none;
  border-radius: 16px;
  padding: 16px;
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(24, 111, 122, 0.3);
  transition: transform 0.15s, background 0.2s;
  margin-top: 8px;
  
  &:hover { background: #C85E30; transform: translateY(-2px); }
  &:active { transform: translateY(1px); }
  
  &:disabled {
    background: #C8C0A8;
    box-shadow: none;
    cursor: not-allowed;
    transform: none;
  }
`;

const AutoLoginGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
`;

const Checkbox = styled.input`
  accent-color: #186F7A;
  width: 16px;
  height: 16px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 13px;
  color: #5C5040;
  cursor: pointer;
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  margin: 24px 0;
  color: #C8C0A8;
  font-size: 13px;
  
  &::before, &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid #D8D0B8;
  }
  
  &:not(:empty)::before {
    margin-right: .5em;
  }
  
  &:not(:empty)::after {
    margin-left: .5em;
  }
`;

const SocialGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 32px;
`;

const SocialBtn = styled.button<{ $provider: 'kakao' | 'google' | 'apple' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  padding: 14px;
  border-radius: 16px;
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.1s;
  border: none;
  
  ${({ $provider }) => {
    switch ($provider) {
      case 'kakao':
        return 'background: #FEE500; color: #000000;';
      case 'google':
        return 'background: #FFFFFF; color: #000000; border: 1px solid #D8D0B8;';
      case 'apple':
        return 'background: #000000; color: #FFFFFF;';
      default:
        return '';
    }
  }}

  &:hover {
    opacity: 0.9;
  }
  &:active {
    transform: scale(0.98);
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
`;

const SignupPrompt = styled.div`
  text-align: center;
  font-size: 14px;
  color: #5C5040;
  margin-top: auto;
  padding-top: 24px;
  
  a {
    color: #E07040;
    font-weight: 700;
    text-decoration: none;
    margin-left: 6px;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

/* ─── Component ──────────────────────────────────────────────────────────── */
const LoginMain: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [autoLogin, setAutoLogin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [popup, setPopup] = useState<{ title: string; msg: string } | null>(null);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/user/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user_email: email, password }),
      });

      if (response.ok) {
        const data = await response.json().catch(() => null);

        // 이전 로그인 방식의 만료된 값이 새 토큰보다 먼저 선택되지 않도록
        // 로그인 성공 시 두 저장소의 기존 인증 상태를 먼저 정리한다.
        localStorage.removeItem('isLoggedIn');
        localStorage.removeItem('access_token');
        sessionStorage.removeItem('isLoggedIn');
        sessionStorage.removeItem('access_token');

        if (autoLogin) {
          localStorage.setItem('isLoggedIn', 'true');
          if (data?.access_token) localStorage.setItem('access_token', data.access_token);
        } else {
          sessionStorage.setItem('isLoggedIn', 'true');
          if (data?.access_token) sessionStorage.setItem('access_token', data.access_token);
        }
        

        window.location.replace('/');
      } else {
        setPopup({ title: '로그인 안내', msg: t.login.alertFail });
      }
    } catch (error) {
      console.error('Login error:', error);
      setPopup({ title: '로그인 안내', msg: t.login.alertError });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialClick = (provider: string) => {
    setPopup({ title: '로그인 안내', msg: `${provider}${t.login.alertSocial}` });
  };

  return (
    <>
      <GlobalStyle />
      <ResponsiveShell>
        <PhoneNavBar>
          <BackBtn onClick={() => { window.location.href = '/'; }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </BackBtn>
          <NavTitle>{t.login.title}</NavTitle>
        </PhoneNavBar>

        <PhoneBody>
          <FormContainer onSubmit={handleLogin}>
            <InputGroup>
              <InputLabel>{t.login.emailLabel}</InputLabel>
              <StyledInput
                type="email"
                placeholder={t.login.emailPlaceholder}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </InputGroup>
            
            <InputGroup>
              <InputLabel>{t.login.passwordLabel}</InputLabel>
              <StyledInput
                type="password"
                placeholder={t.login.passwordPlaceholder}
                value={password}
                onChange={(e) => setPassword(e.target.value.replace(/[^\x00-\x7F]/g, ''))}
                required
              />
            </InputGroup>

            <AutoLoginGroup>
              <Checkbox 
                type="checkbox" 
                id="autoLogin" 
                checked={autoLogin} 
                onChange={(e) => setAutoLogin(e.target.checked)} 
              />
              <CheckboxLabel htmlFor="autoLogin">{t.login.autoLogin}</CheckboxLabel>
            </AutoLoginGroup>

            <PrimaryBtn type="submit" disabled={!email || !password || isLoading}>
              {isLoading ? t.login.btnLoading : t.login.btnSubmit}
            </PrimaryBtn>
          </FormContainer>

          <Divider>{t.login.dividerText}</Divider>

          <SocialGroup>
            <SocialBtn type="button" $provider="kakao" onClick={() => handleSocialClick('카카오')}>
              <IconWrapper>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 3C6.477 3 2 6.51 2 10.84c0 2.76 1.83 5.17 4.6 6.54-.23.86-1.12 4.19-1.17 4.41-.06.26.1.25.26.15.13-.08 4.14-2.8 5.76-3.88.5.06 1.02.1 1.55.1 5.523 0 10-3.51 10-7.84S17.523 3 12 3z"/>
                </svg>
              </IconWrapper>
              {t.login.kakaoBtn}
            </SocialBtn>
            
            <SocialBtn type="button" $provider="google" onClick={() => handleSocialClick('Google')}>
              <IconWrapper>
                <svg viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  <path fill="none" d="M1 1h22v22H1z"/>
                </svg>
              </IconWrapper>
              {t.login.googleBtn}
            </SocialBtn>

            <SocialBtn type="button" $provider="apple" onClick={() => handleSocialClick('Apple')}>
              <IconWrapper>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16.146 11.55c0-2.31 1.886-3.41 1.97-3.463-1.077-1.573-2.75-1.79-3.344-1.815-1.423-.143-2.776.837-3.504.837-.73 0-1.844-.82-3.013-.797-1.517.022-2.92.882-3.7 2.235-1.564 2.715-.4 6.732 1.127 8.93 1.008 1.455 2.15 2.455 3.344 2.433 1.144-.022 1.583-.715 2.946-.715 1.362 0 1.777.715 2.968.693 1.21-.022 2.203-1.006 3.076-2.28.905-1.32 1.28-2.6 1.298-2.668-.028-.014-2.5-1.01-2.5-3.83z" />
                  <path d="M14.653 7.82c.626-.763 1.05-1.82.936-2.88-1.025.04-2.128.68-2.775 1.442-.577.68-1.085 1.745-.948 2.784 1.146.088 2.162-.584 2.787-1.346z" />
                </svg>
              </IconWrapper>
              {t.login.appleBtn}
            </SocialBtn>
          </SocialGroup>

          <SignupPrompt>
            {t.login.noAccount} 
            <a href="/signup" onClick={(e) => { e.preventDefault(); window.location.href = '/signup'; }}>
              {t.login.goSignup}
            </a>
          </SignupPrompt>
        </PhoneBody>

        {/* ── 인라인 팝업 ── */}
        {popup && (
          <div style={{
            position: 'fixed', inset: 0,
            background: 'rgba(40,30,20,0.55)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px', boxSizing: 'border-box'
          }}>
            <div style={{
              background: '#FDFBF4', borderRadius: '20px', padding: '28px 24px 20px',
              width: '90%', maxWidth: '340px', boxShadow: '0 16px 40px rgba(40,30,20,0.3)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center'
            }}>
              <div style={{
                width: 48, height: 48, borderRadius: '50%', background: '#FFF0EA',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 14, color: '#E07040', flexShrink: 0
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                </svg>
              </div>
              <p style={{ fontWeight: 700, fontSize: 17, color: '#3D3020', marginBottom: 8 }}>{popup.title}</p>
              <p style={{ fontSize: 14, color: '#6C5D47', lineHeight: 1.6, marginBottom: 22, whiteSpace: 'pre-line' }}>{popup.msg}</p>
              <button
                onClick={() => setPopup(null)}
                style={{
                  width: '100%', padding: '13px', borderRadius: 12, border: 'none', cursor: 'pointer',
                  background: 'linear-gradient(135deg,#E07040,#D05828)', color: '#fff',
                  fontSize: 15, fontWeight: 700, boxShadow: '0 4px 14px rgba(224,112,64,0.3)'
                }}
              >확인</button>
            </div>
          </div>
        )}
      </ResponsiveShell>
    </>
  );
};

export default LoginMain;
